import { Link, createFileRoute } from "@tanstack/react-router";
import { AlertTriangleIcon, ArrowLeftIcon, InfoIcon, ShieldIcon } from "lucide-react";

import { CopyButton } from "~/components/copy-button";
import { DetailField, FieldGrid } from "~/components/detail-field";
import { JsonViewer } from "~/components/json-viewer";
import { PageHeader } from "~/components/page-header";
import { StatusBadge } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { errorMessage, isNotFound } from "~/lib/api";
import { EM_DASH, formatDateTime, formatRelative, orDash } from "~/lib/format";
import { useWebhookEvent, type WebhookEventDetail } from "~/lib/queries";
import { RetryWebhookAction } from "./-retry-action";

export const Route = createFileRoute("/_authed/webhook-events/$eventId")({
  component: WebhookEventDetailPage,
});

/**
 * One inbound webhook, whole.
 *
 * Built around the question that brings people here: the provider says they told
 * us — did they, and what did we do about it? So the processing error comes
 * first when there is one, the raw payload is open rather than one click away,
 * and the route back to the order is a link rather than a copied id.
 */
function WebhookEventDetailPage() {
  const { eventId } = Route.useParams();
  const { data, error, isPending, refetch } = useWebhookEvent(eventId);

  if (isPending) return <DetailSkeleton />;

  if (isNotFound(error)) {
    return (
      <EmptyState
        title="No such webhook event"
        body={
          <>
            Nothing in <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              webhook_events
            </code>{" "}
            has this id. The table is append-only and nothing deletes from it, so this id has
            never existed.
          </>
        }
      />
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        title="Couldn't load this webhook event"
        body={errorMessage(error)}
        onRetry={() => void refetch()}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" render={<Link to="/webhook-events" />}>
          <ArrowLeftIcon className="size-3.5" />
          All webhook events
        </Button>
      </div>

      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <span>{data.webhook_type}</span>
            {data.event_type ? (
              <span className="font-mono text-base text-muted-foreground">
                {data.event_type}
              </span>
            ) : null}
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={data.status} />
            <span title={formatDateTime(data.received_at)}>
              received {formatRelative(data.received_at)}
            </span>
            <span className="text-muted-foreground">from {orDash(data.source)}</span>
          </span>
        }
        actions={data.retryable ? <RetryWebhookAction event={data} /> : null}
      />

      {/* First on the page when it exists. This is the payload of the whole
          screen: it is the sentence that explains why the order is stuck. */}
      {data.processing_error ? <ProcessingError detail={data} /> : null}
      {data.outcome?.startsWith("ignored:") ? <IgnoredOutcome outcome={data.outcome} /> : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-5">
          <RawPayload detail={data} />
          <Headers detail={data} />
        </div>

        <div className="space-y-5">
          <Correlation detail={data} />
          <Delivery detail={data} />
        </div>
      </div>
    </div>
  );
}

function ProcessingError({ detail }: { detail: WebhookEventDetail }) {
  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-destructive">
          <AlertTriangleIcon className="size-4" />
          Processing failed
        </CardTitle>
        <CardDescription>
          Altura received this webhook and could not act on it. The provider was still answered,
          so it will not necessarily retry.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-1">
          <pre className="min-w-0 flex-1 overflow-x-auto rounded-md border border-destructive/20 bg-background p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap">
            {detail.processing_error}
          </pre>
          <CopyButton value={detail.processing_error ?? ""} label="processing error" />
        </div>
      </CardContent>
    </Card>
  );
}

const ignoredReasons: Record<string, string> = {
  payment_link_attempt:
    "It reports one payment attempt on the DurianPay payment-link page. The buyer can still pay with another method on the same order.",
  attempt_expired_order_live:
    "One payment attempt expired, but the order itself was still inside its expiry window.",
  other_payment_id:
    "It is about a different DurianPay payment than the one this transaction holds.",
};

function IgnoredOutcome({ outcome }: { outcome: string }) {
  const reason = outcome.slice("ignored:".length);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Acknowledged, not applied</CardTitle>
        <CardDescription>
          {ignoredReasons[reason] ?? reason} The transaction status was left unchanged and the
          merchant was not notified.
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

function RawPayload({ detail }: { detail: WebhookEventDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Raw payload</CardTitle>
        <CardDescription>
          Exactly what arrived, unmodified. This is the artifact to paste into a support thread.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Open by default and searchable: on this screen the payload is not the
            escape hatch, it is the point. */}
        <JsonViewer
          value={detail.raw_payload}
          label="raw_payload"
          defaultOpen
          searchable
        />
      </CardContent>
    </Card>
  );
}

function Headers({ detail }: { detail: WebhookEventDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Headers</CardTitle>
        <CardDescription>
          The <code className="font-mono text-xs">X-</code> headers, content type and user agent
          captured from the request.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Said plainly rather than left for someone to notice. A blob that
            silently differs from what arrived undermines the one thing this
            page is for. */}
        {detail.headers_redacted ? (
          <p className="flex items-start gap-2 rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground">
            <ShieldIcon className="mt-0.5 size-3.5 shrink-0" />
            <span>
              At least one header value is shown as{" "}
              <code className="font-mono">[redacted]</code> because its name looks like a
              credential. Signatures, timestamps and partner identifiers are{" "}
              <strong className="font-medium text-foreground">not</strong> redacted — those are
              the evidence, not the secret.
            </span>
          </p>
        ) : null}

        <JsonViewer value={detail.headers} label="headers" searchable />
      </CardContent>
    </Card>
  );
}

/**
 * The round trip between "this webhook arrived" and "here is what the order
 * looks like", which is the core loop of the debugging job.
 */
function Correlation({ detail }: { detail: WebhookEventDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Correlation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGrid className="sm:grid-cols-1 lg:grid-cols-1">
          <DetailField label="Order ref" copy={detail.order_ref_id} mono>
            {detail.order_ref_id == null ? (
              <span className="text-muted-foreground">{EM_DASH}</span>
            ) : detail.order_ref_resolved ? (
              <Link
                to="/transactions/$orderId"
                params={{ orderId: detail.order_ref_id }}
                className="text-primary hover:underline"
              >
                {detail.order_ref_id}
              </Link>
            ) : (
              <span className="text-muted-foreground">{detail.order_ref_id}</span>
            )}
          </DetailField>

          <DetailField label="External ID" copy={detail.external_id} mono>
            {orDash(detail.external_id)}
          </DetailField>

          <DetailField label="Dedupe key" copy={detail.dedupe_key} mono>
            {orDash(detail.dedupe_key)}
          </DetailField>
        </FieldGrid>

        {/* An unresolved reference is a finding, not a rendering edge case:
            the provider named an order this database does not have. */}
        {detail.order_ref_id != null && !detail.order_ref_resolved ? (
          <p className="rounded-md border border-warning/30 bg-warning/10 p-2.5 text-xs text-muted-foreground">
            No transaction in Altura has this order id. The provider referenced an order we
            never created, or it was created under a different id.
          </p>
        ) : null}

        {/* The null case is the one worth explaining. It is unique per
            (source, dedupe_key), and NULLs do not collide in a unique index --
            so a null key means this delivery was never deduplicated against
            anything, which is what explains a double-processing mystery. */}
        {detail.dedupe_key == null ? (
          <p className="flex items-start gap-2 rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground">
            <InfoIcon className="mt-0.5 size-3.5 shrink-0" />
            <span>
              <strong className="font-medium text-foreground">No dedupe key.</strong> One could
              not be derived from this payload, so this delivery was not checked against events
              already seen. If the same event arrived twice, both were processed.
            </span>
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Delivery({ detail }: { detail: WebhookEventDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Delivery</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FieldGrid className="sm:grid-cols-2 lg:grid-cols-2">
          <DetailField label="Received">
            <span title={formatDateTime(detail.received_at)}>
              {formatRelative(detail.received_at)}
            </span>
          </DetailField>
          <DetailField label="Processed">{formatDateTime(detail.processed_at)}</DetailField>
          <DetailField label="Source IP" copy={detail.ip_address} mono>
            {orDash(detail.ip_address)}
          </DetailField>
          <DetailField label="Retry count">{detail.retry_count}</DetailField>
          <DetailField label="Signature">{orDash(detail.signature_status)}</DetailField>
          <DetailField label="Endpoint" copy={detail.endpoint} mono className="col-span-full">
            {detail.endpoint}
          </DetailField>
        </FieldGrid>

        {/* Turns "why are there five of these?" into an obvious answer rather
            than an investigation. */}
        <p className="flex items-start gap-2 rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground">
          <InfoIcon className="mt-0.5 size-3.5 shrink-0" />
          <span>
            DurianPay redelivers an unacknowledged webhook at{" "}
            <strong className="font-medium text-foreground">2, 5, 10, 90 and 210 minutes</strong>{" "}
            until it sees a 200. Several near-identical events are that schedule, not a bug.
          </span>
        </p>
      </CardContent>
    </Card>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-8 w-64" />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-5">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
        <div className="space-y-5">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-56 w-full" />
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  title,
  body,
  onRetry,
}: {
  title: string;
  body: React.ReactNode;
  onRetry?: () => void;
}) {
  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          {onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" render={<Link to="/webhook-events" />}>
            Back to webhook events
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
